export enum ProjectStatusEnum {
  DRAFT = 'draft',
  VERIFICATION_PROCESSED = 'verification_processed',
  CONSTRUCTION_STARTED = 'construction_started',
  IN_SERVICE = 'in_service',
  ARCHIVED = 'archived',
}

export enum ProjectStatusDialogEnum {
  DRAFT = 'Draft',
  VERIFICATION = 'Development',
  CONSTRUCTION_STARTED = 'Construction',
  IN_SERVICE = 'Service',
  ARCHIVED = 'Archived',
}

export enum MapLayerEnum {
  // MECHANICAL
  HOLES = 'holes',
  PIERS = 'piers',
  TRACKERS = 'trackers',
  TABLES = 'tables',
  MODULES = 'modules',
  // ELECTRICAL
  CBX = 'cbx', // DC Harnesses/CBXs
  DC_HARNESSES = 'dc_harnesses', // DC Feeders AG
  DC_FEEDERS = 'dc_feeders', // DC Feeders UG
  AC_STATIONS = 'ac_stations',
  MV_FEEDERS = 'mv_feeders',
  BESS = 'bess',
  SUBSTATIONS = 'substations',
  // GROUPS
  AREA = 'areas',
  FEEDERS = 'feeders',
  PCS = 'pcs',
  COMBINER_BOXES = 'combiner_boxes',
  SITE = 'sites',
  SUBSTATION_GROUP = 'substation',
  // CIVIL
  ROADS = 'roads',
  FENCES = 'fences',
  MVT_FENCES = 'boundaries',

  // SUBSTATION (DT3) sub-elements — values must equal the digital-twin-structure.json keys
  CONTROL_BUILDING_FOUNDATION = 'control_building_foundations',
  CONTROL_BUILDING = 'control_buildings',
  GROUND_GRID = 'ground_grids',
  MPT_FOUNDATION = 'mpt_foundations',
  MPT = 'mpt',
  CAPACITOR_BANK = 'capacitor_banks',
  CAPACITOR_BANK_FOUNDATION = 'capacitor_bank_foundations',
  OTHER_FOUNDATION = 'other_foundations',
  HI_SIDE_BREAKER = 'hi_side_breakers',
  LO_SIDE_BREAKER = 'lo_side_breakers',

  QC_ISSUE = 'qc_issue',
}

/**
 * `element_type` (as returned by the combined MVT tile, and by the element APIs) → the map layer
 * that owns it. The single `elements` vector-tile layer dispatches its style function through
 * this, and it maps a selected feature back onto a MapLayerEnum for the side drawers.
 *
 * Values are verified against real tile data. Note that the tile emits `project` for the site
 * group and `boundary` for fences — neither is a plain singularization of the layer name.
 *
 * `qc_issue` is deliberately absent: the combined endpoint does not serve QC observations, they
 * keep their own layer in the QC configs. Anything not listed here is left unstyled rather than
 * guessed at, so a new element type shows up as missing instead of drawn as the wrong thing.
 */
export const ELEMENT_TYPE_TO_MAP_LAYER: Record<string, MapLayerEnum> = {
  // MECHANICAL
  hole: MapLayerEnum.HOLES,
  pier: MapLayerEnum.PIERS,
  tracker: MapLayerEnum.TRACKERS,
  table: MapLayerEnum.TABLES,
  module: MapLayerEnum.MODULES,
  // ELECTRICAL
  cbx: MapLayerEnum.CBX,
  dc_harness: MapLayerEnum.DC_HARNESSES,
  dc_feeder: MapLayerEnum.DC_FEEDERS,
  ac_station: MapLayerEnum.AC_STATIONS,
  mv_feeder: MapLayerEnum.MV_FEEDERS,
  bess: MapLayerEnum.BESS,
  substation: MapLayerEnum.SUBSTATIONS,
  // GROUPS
  area: MapLayerEnum.AREA,
  feeder: MapLayerEnum.FEEDERS,
  pcs: MapLayerEnum.PCS,
  combiner_box: MapLayerEnum.COMBINER_BOXES,
  site: MapLayerEnum.SITE,
  // * project -> site because on a backend side project is a site
  project: MapLayerEnum.SITE,
  // CIVIL
  road: MapLayerEnum.ROADS,
  fence: MapLayerEnum.FENCES,
  boundary: MapLayerEnum.FENCES,
  // SUBSTATION (DT3) sub-elements
  control_building_foundation: MapLayerEnum.CONTROL_BUILDING_FOUNDATION,
  control_building: MapLayerEnum.CONTROL_BUILDING,
  ground_grid: MapLayerEnum.GROUND_GRID,
  mpt_foundation: MapLayerEnum.MPT_FOUNDATION,
  mpt: MapLayerEnum.MPT,
  capacitor_bank: MapLayerEnum.CAPACITOR_BANK,
  capacitor_bank_foundation: MapLayerEnum.CAPACITOR_BANK_FOUNDATION,
  other_foundation: MapLayerEnum.OTHER_FOUNDATION,
  hi_side_breaker: MapLayerEnum.HI_SIDE_BREAKER,
  lo_side_breaker: MapLayerEnum.LO_SIDE_BREAKER,
};

export enum CivilMapLayerEnum {
  ROADS = 'roads',
  FENCES = 'fences',
}

export enum ElectricalMapLayerEnum {
  CBX = 'cbx',
  DC_HARNESSES = 'dc_harnesses',
  DC_FEEDERS = 'dc_feeders',
  AC_STATIONS = 'ac_stations',
  MV_FEEDERS = 'mv_feeders',
  BESS = 'bess',
  SUBSTATIONS = 'substations',
}

export enum MechanicalMapLayerEnum {
  HOLES = 'holes',
  PIERS = 'piers',
  TRACKERS = 'trackers',
  TABLES = 'tables',
  MODULES = 'modules',
}

export enum WbsScopeEnum {
  ELEMENT_TYPE_ROAD = 'road',
  ELEMENT_TYPE_FENCE = 'fence',
  ELEMENT_TYPE_HOLE = 'hole',
  ELEMENT_TYPE_PIER = 'pier',
  ELEMENT_TYPE_TRACKER = 'tracker',
  ELEMENT_TYPE_TABLE = 'table',
  ELEMENT_TYPE_MODULE = 'module',
  ELEMENT_TYPE_SUBSTATION = 'substation',
  ELEMENT_TYPE_COMBINER_BOX = 'combiner_box',
  ELEMENT_TYPE_DC_FEEDER_AG = 'dc_feeder_ag',
  ELEMENT_TYPE_DC_FEEDER_UG = 'dc_feeder_ug',
  ELEMENT_TYPE_AC_STATION = 'ac_station',
  ELEMENT_TYPE_MV_FEEDER = 'mv_feeder',
  ELEMENT_TYPE_MV_BESS = 'bess',
}

export enum MapLayerGroupEnum {
  CIVIL = 'civil',
  MECHANICAL = 'mechanical',
  ELECTRICAL = 'electrical',
  SUBSTATION = 'substation',
  GROUP_ELEMENTS = 'group_elments',
  SCADA = 'scada',
}

export enum QCIssuesStateEnum {
  DRAFT = 'draft',
  CLOSED = 'closed',
  RESOLVED = 'resolved',
  OPEN = 'opened',
}

export enum QCIssuesStateLabelEnum {
  DRAFT = 'Draft',
  OPEN = 'Open',
  RESOLVED = 'Remediated',
  CLOSED = 'Verified',
}

export enum WBSEElementTypeEnum {
  MECHANICAL = 'mechanical',
  ELECTRICAL = 'electrical',
  CIVIL = 'civil',
  SUBSTATION = 'substation',
  GROUPS = 'groups',
}

export enum FloatingGuaranteedMilestone {
  FLOATING = 'floating',
  GUARANTEED = 'guaranteed',
}

export enum DictionaryPipeType {
  SINGULAR = 'singular',
  PLURAL = 'plural',
  ABBREVIATION = 'abbreviation',
}

export enum BaseMapType {
  SAT = 'sat',
  DARK = 'dark',
  TOPO = 'topo',
  OSM = 'osm',
}

export enum DigitalTwinMapMultipleOptions {
  MULTIPLE_SELECT = 'multipleSelect',
  MULTIPLE_DESELECT = 'multipleDeselect',
}

export enum GeometryTypeEnum {
  Length = 'LineString',
  Point = 'Point',
  Area = 'Polygon',
}

export enum MvCircuitStatusColor {
  IN_SERVICE_EPC_BORDER = '#B26F21',
  IN_SERVICE_EPC_FILL = '#FE9E2F',
  IN_SERVICE_OM_BORDER = '#45731F',
  IN_SERVICE_OM_FILL = '#E1EFD6',
  HOT_COMMISSIONING_BORDER = '#E09400',
  HOT_COMMISSIONING_FILL = '#FFCC69',
  IN_CONSTRUCTION_BORDER = '#2B5390',
  IN_CONSTRUCTION_FILL = '#8098BC',
  NOT_STARTED_BORDER = '#808080',
  NOT_STARTED_FILL = '#E4E4E4',
}

export enum ProjectDataSource {
  DUAL = 'dual',
  OWNER = 'owner',
  EPC = 'epc',
}

export enum MeasurementUnits {
  Metric = 'metric',
  Imperial = 'imperial',
}

export enum DroneOrthoSourcesEnum {
  Maptiler = 'maptiler',
  S3 = 's3',
  S3Direct = 's3direct',
}

export enum InstallationElementsLegendEnum {
  NOT_INSTALLED = 'Not Installed (IFC)',
  PLAN = 'Plan',
  IN_PROGRESS = 'In Progress',
  INSTALLED = 'Installed',
}

export enum InstallationProgressEnum {
  IFC = 'ifc',
  PLANNED = 'planned',
  IN_PROGRESS = 'inProgress',
  INSTALLED = 'installed',
}

export enum OrthoImageTypeEnum {
  Map = 'map',
  Elevation = 'elevation',
  CutAndFill = 'cut_and_fill',
  IrThermal = 'ir_thermal',
}

export enum OrthoImageProcessingStatus {
  NOT_INITIATED = 'not_initiated',
  IN_PROCESS = 'in_process',
  FINISHED = 'finished',
  FAILED = 'failed',
}

export type ConstructionProjectFeaturesAccessMap = Record<ConstructionProjectFeatureKey, boolean>;

export enum ConstructionProjectFeatureKey {
  LOGISTICS_INVENTORY = 'logistics_inventory',
  QC_ISSUES = 'qc_issues',
  DIGITAL_TWIN = 'digital_twin',
  PRODUCTION_TRACKING = 'production_tracking',
  EQUIPMENT_TRACKING = 'equipment_tracking',
  APPROVALS = 'approvals',
  OMEGA = 'omega',
  MV_CIRCUIT = 'mv_circuit',
  ADVANCED_DETECTION = 'advanced_detection',
  TERAFAB = 'terafab',
  DRONE_IMAGERY = 'drone_imagery',
  DOCUMENTS = 'documents',
}

/**
 * Display/sort order for project features, keyed by feature.
 * Typed as `Record<ConstructionProjectFeatureKey, number>` so adding a new
 * `ConstructionProjectFeatureKey` without an order here is a compile-time error.
 */
export const constructionProjectFeaturesOrder: Record<ConstructionProjectFeatureKey, number> = {
  [ConstructionProjectFeatureKey.DRONE_IMAGERY]: 0,
  [ConstructionProjectFeatureKey.LOGISTICS_INVENTORY]: 1,
  [ConstructionProjectFeatureKey.DIGITAL_TWIN]: 2,
  [ConstructionProjectFeatureKey.PRODUCTION_TRACKING]: 3,
  [ConstructionProjectFeatureKey.EQUIPMENT_TRACKING]: 4,
  [ConstructionProjectFeatureKey.OMEGA]: 5,
  [ConstructionProjectFeatureKey.ADVANCED_DETECTION]: 6,
  [ConstructionProjectFeatureKey.QC_ISSUES]: 7,
  [ConstructionProjectFeatureKey.DOCUMENTS]: 8,
  [ConstructionProjectFeatureKey.APPROVALS]: 9,
  [ConstructionProjectFeatureKey.MV_CIRCUIT]: 10,
  [ConstructionProjectFeatureKey.TERAFAB]: 11,
};

export type WBSElementDataType = {
  id: string;
  layer: string;
  layerName?: string;
  name: string;
  built_at?: any;
  epc_built_at?: any;
  draft_built_at?: any;
  [k: string]: any;
};

export type IWBSElementDef = {
  label: string;
  layer: string;
  type: string;
  group: boolean;
  elements: WBSElementDataType[];
  flagged?: boolean;
  dictionaryLabel?: string;
  statuses?: {
    installed: number;
    in_progress: number;
    not_installed: number;
  };
};

export type SectionLabelsType = {
  civil: string;
  mechanical: string;
  electrical: string;
  group: string;
  // Optional: BE does not emit a substation section label yet (DT3). Falls back to a static label.
  substation?: string;
};

export enum WBSElementType {
  ELECTRICAL = 'electrical',
  MECHANICAL = 'mechanical',
  GROUPING = 'grouping',
  ORGANIZATIONAL = 'organizational',
  CIVIL = 'civil',
  SUBSTATION = 'substation',
}

export enum OrthoStatusEnum {
  DRAFT = 'draft',
  PUBLISHED = 'published',
}

export enum PanoStatusEnum {
  DRAFT = 'draft',
  PUBLISHED = 'published',
}

export enum ConstructionProjectType {
  Solar = 'solar',
  Bess = 'bess',
  Wind = 'wind',
}
